/* eslint-disable */
import React, { useEffect, useRef } from "react";
import { dia, shapes } from "jointjs";
import IconCluster from "@assets/Icons/Icon_ctx.png";
import { GenericTag } from "@ui-components/Tag/GenericTag";

type PeerStatusTag = Pick<React.ComponentProps<typeof GenericTag>, "label" | "variant">;

// Anything but an explicit true/false (null, undefined) reads as "No data", never a blank cell or "---".
const getPeerStatusTag = (statuspeer: boolean | null | undefined): PeerStatusTag => {
    if (statuspeer === true) return { label: "UP", variant: "success" };
    if (statuspeer === false) return { label: "Down", variant: "error" };
    return { label: "No data", variant: "neutral" };
};

type Props = {
    name: string;
    ctxs: Array<{
        subid: string;
        name: string;
        description: string;
        localpref: number;
        statuspeer: boolean | null;
        status: string;
    }>;
    id: number;
    enable: boolean;
    suscription?: string;
    overallStatus: string;
    setID: (id: number) => void;
    onDelete?: (clusterId: number) => void;
};

export const GroupsWidget: React.FC<Props> = ({
    name,
    ctxs,
    id,
    enable,
    overallStatus,
    setID,
    onDelete,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!containerRef.current) return;
        const graph = new dia.Graph();
        const _paper = new dia.Paper({
            el: containerRef.current,
            model: graph,
            width: 260,
            height: 100,
            interactive: false,
        });
        if (!ctxs || ctxs.length < 2) return;
        const maxLocalPrefElement = ctxs.reduce((max, curr) =>
            max.localpref > curr.localpref ? max : curr
        );
        const statuspeer: Array<boolean | null> = [];
        ctxs.forEach((element) => {
            const image = new shapes.standard.Image();
            const xPos = element === maxLocalPrefElement ? 30 : 180;
            image.position(xPos, 25);
            image.resize(40, 40);
            image.attr({
                image: {
                    "xlink:href": IconCluster,
                    width: 40,
                    height: 40,
                },
            });
            graph.addCell(image);
            statuspeer.push(element.statuspeer);
        });
        const link = new shapes.standard.Link();
        link.source(graph.getElements()[0], {
            anchor: { name: "right", args: { dx: 5 } },
        });
        link.target(graph.getElements()[1], {
            anchor: { name: "left", args: { dx: -5 } },
        });
        if (enable) {
            const [firstStatus, secondStatus] = statuspeer;
            if (firstStatus === true && secondStatus === true) {
                setLinkStyles(link, "HA Up", "#50cd89");
            } else if (
                (firstStatus === true && secondStatus === false) ||
                (firstStatus === false && secondStatus === true)
            ) {
                setLinkStyles(link, "HA Degraded", "#FFA500", true);
            } else if (firstStatus === false && secondStatus === false) {
                setLinkStyles(link, "HA Down", "#FF4089", true);
            } else {
                setLinkStyles(link, "No Connection", "#808080", true);
            }
        } else {
            setLinkStyles(link, "HA Disabled", "#D3D3D3", true);
        }
        link.addTo(graph);
    }, [ctxs, enable]);

    function setLinkStyles(
        linkObj: dia.Link,
        labelText: string,
        color: string,
        dashed?: boolean
    ) {
        linkObj.appendLabel({
            attrs: {
                text: {
                    text: labelText,
                    fontSize: 10,
                    fill: color,
                },
            },
        });
        linkObj.attr({
            line: {
                stroke: color,
                ...(dashed && { strokeDasharray: "5 5" }),
            },
        });
    }

    return (
        <div className="bg-semantic-bg-primary border border-semantic-border-secondary rounded-sm shadow-sm p-4 space-y-3 w-full">
            <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-semantic-text-primary">
                    {name}
                </h2>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => onDelete?.(id)}
                        className="text-red-500 hover:text-red-600 transition-colors"
                        title="Delete Cluster"
                    >
                        <i className="bi bi-trash3 fs-3"></i>
                    </button>
                    <button
                        onClick={() => setID(id)}
                        className="text-azul hover:text-blue-600 transition-colors"
                        title="Edit Cluster"
                    >
                        <i className="bi bi-pencil-square fs-3"></i>
                    </button>
                </div>
            </div>
            <div className="flex justify-center">
                <div
                    ref={containerRef}
                    className="border border-semantic-border-secondary rounded-sm"
                    style={{ width: 260, height: 100 }}
                />
            </div>
            <div className="flex justify-evenly text-sm">
                {ctxs.map((c) => (
                    <div key={c.subid} className="text-center">
                        <div className="font-semibold text-semantic-text-primary">{c.name}</div>
                        <div className="text-xs text-semantic-text-secondary">
                            ({c.description})
                        </div>
                    </div>
                ))}
            </div>
            <table className="w-full border border-semantic-border-secondary text-sm">
                <thead className="bg-semantic-bg-secondary text-semantic-text-secondary">
                    <tr>
                        <th className="py-1 px-2 border-b border-semantic-border-secondary text-left">
                            Description
                        </th>
                        <th className="py-1 px-2 border-b border-semantic-border-secondary text-left">
                            Priority
                        </th>
                        <th className="py-1 px-2 border-b border-semantic-border-secondary text-left">
                            HA status
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {ctxs.map((c) => (
                        <tr
                            key={c.subid}
                            className="hover:bg-semantic-state-active transition-colors"
                        >
                            <td className="py-1 px-2 border-b border-semantic-border-secondary text-semantic-text-primary">
                                {c.description}
                            </td>
                            <td className="py-1 px-2 border-b border-semantic-border-secondary text-semantic-text-primary">
                                {c.localpref}
                            </td>
                            <td className="py-1 px-2 border-b border-semantic-border-secondary">
                                <GenericTag {...getPeerStatusTag(c.statuspeer)} size="small" />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {overallStatus && (
                <div className="text-xs text-semantic-text-secondary italic">{overallStatus}</div>
            )}
        </div>
    );
};
